import React, { useRef } from "react";
import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import DragRail from "web/react/components/drag-rail/drag-rail";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import RailLinkItem from "web/react/components/rail-link-item/rail-link-item";
import { ReduxStoreState } from "web/redux/store";
import { connect, useDispatch } from "react-redux";
import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import withReduxProvider from "web/react/redux-provider";
import { closeAssistedSearch } from "web/redux/ducks/app-header-search";
import { RawBranchLink } from "web/script/utils/branch-io";
import RelatedDesignersRail from "web/react/components/related-designers/related-designers-rail";
import SendToAppBanner from "web/react/components/send-to-app-banner/send-to-app-banner";
import { AssistedSearchSerializer } from "web/types/serializers";
import styles from "./assisted-search.css";

interface AssistedSearchState {
    isOpen: boolean;
    selectedGender: string;
}

interface AssistedSearchProps extends AssistedSearchState {
    defaultGender: string;
}

function mapStateToProps(state: ReduxStoreState): AssistedSearchState {
    return {
        isOpen: state.appHeaderSearchReducer.assistedSearchOpen,
        selectedGender: state.mainNavigationReducer.selectedGender,
    };
}

export function AssistedSearch({
    isOpen,
    selectedGender,
    defaultGender,
}: AssistedSearchProps): JSX.Element | null {
    const dispatch = useDispatch();
    const assistedSearchElementRef = useRef<HTMLDivElement>(null);
    const analyticsCategory = "assisted_search";

    // fallback to 'women' if no defaultGender has been supplied
    const currentGender = (selectedGender || defaultGender || "women").toLowerCase();
    const { data: rails, isFetching } = useRothko<AssistedSearchSerializer>(
        "modules/assisted_search",
        { gender: currentGender },
        { enabled: isOpen }
    );
    const { top_rail: topRail, generic_category_rails: genericCategoryRails } = rails || {};
    const dataExists =
        topRail !== undefined &&
        topRail.links.length !== 0 &&
        genericCategoryRails !== undefined &&
        genericCategoryRails.length !== 0;

    // don't show assisted search if no rail data exists or show loading spinner if still fetching
    if (!dataExists && !isFetching) {
        return null;
    }

    const show = isOpen && (dataExists || isFetching);

    if (assistedSearchElementRef.current !== null) {
        assistedSearchElementRef.current.classList.toggle(styles.fadeOut, !show);
        assistedSearchElementRef.current.classList.toggle(styles.fadeIn, show);

        if (show) {
            assistedSearchElementRef.current.classList.remove(styles.hide);
        }
    }

    function onAnimationEnd(): void {
        if (assistedSearchElementRef.current === null) {
            return;
        }

        assistedSearchElementRef.current.classList.toggle(styles.hide, !show);

        if (isOpen) {
            analytics.event(analyticsCategory, "shown", "", true);
        }
    }

    function onClose(): void {
        analytics.event(analyticsCategory, "closed");
        dispatch(closeAssistedSearch());
    }

    function onTopDragRailClicked(): void {
        analytics.event(analyticsCategory, "clicked", "top_rail_item_clicked");
    }

    return (
        <div
            ref={assistedSearchElementRef}
            onAnimationEnd={onAnimationEnd}
            className={classnames(styles.assistedSearchContainer, styles.hide)}
        >
            <div className={styles.contentContainer}>
                {isFetching && <LoadingSpinner />}
                {!isFetching && (
                    <>
                        <CloseButton onClick={onClose} className={styles.closeButton} />
                        <div className={styles.productRailsContainer}>
                            <h3 className={styles.railTitle}>{topRail?.title}</h3>
                            <DragRail id="assisted-search-dc-rail" className={styles.dragRail}>
                                <div
                                    className={styles.itemsContainer}
                                    onClick={onTopDragRailClicked}
                                >
                                    {topRail?.links.map((topRailLink) => {
                                        return (
                                            <RailLinkItem
                                                className={styles.railLink}
                                                titleClassName={styles.railLinkTitle}
                                                imageClassName={styles.railLinkImage}
                                                key={topRailLink.title}
                                                link={topRailLink}
                                            />
                                        );
                                    })}
                                </div>
                            </DragRail>
                            {genericCategoryRails?.map((rail) => {
                                return (
                                    <React.Fragment key={`${currentGender}_${rail.title}`}>
                                        <h3 className={styles.railTitle}>{rail.title}</h3>
                                        <RelatedDesignersRail
                                            designers={rail.designers}
                                            features={{}}
                                            eventCategory={analyticsCategory}
                                            className={styles.relatedRail}
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </div>
                        <SendToAppBanner
                            className={styles.appBanner}
                            analyticsLabel={analyticsCategory}
                            branchLink={RawBranchLink.AssistedSearch}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default withReduxProvider(connect(mapStateToProps)(AssistedSearch));
